import { useEffect, useState } from 'react';
import {
  Box, Select, MenuItem, FormControl, Button, TextField, Snackbar,
  IconButton, Tooltip, InputAdornment
} from '@mui/material';
import ReactCountryFlag from 'react-country-flag';
import { makeStyles } from 'tss-react/mui';
import CloseIcon from '@mui/icons-material/Close';
import VpnLockIcon from '@mui/icons-material/VpnLock';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { sessionActions } from '../store';
import { useLocalization, useTranslation } from '../common/components/LocalizationProvider';
import LoginLayout from './LoginLayout';
import AuthCard from '../common/auth/AuthCard';
import LogoImage from '../common/auth/LogoImage';
import usePersistedState from '../common/util/usePersistedState';
import {
  generateLoginToken, handleLoginTokenListeners, nativeEnvironment, nativePostMessage,
} from '../common/components/NativeInterface';
import { useCatch } from '../reactHelper';
import AccountCircle from '@mui/icons-material/AccountCircle';
import LockIcon from '@mui/icons-material/Lock';
import QrCodeDialog from '../common/components/QrCodeDialog';
import fetchOrThrow from '../common/util/fetchOrThrow';
import PlayStoreImage from '../resources/images/custom/playstore.svg';
import AppStoreImage from '../resources/images/custom/applestore.svg';

const useStyles = makeStyles()((theme) => ({
  options: {
    position: 'fixed',
    top: theme.spacing(2),
    right: theme.spacing(2),
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    borderRadius: '8px',
    background: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease',
    '&:focus-within': {
      background: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(0, 0, 0, 0.08)',
      boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
    },
  },
  loginButton: {
    background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: 'white',
    fontWeight: 'bold',
    padding: '14px 0',
    borderRadius: '8px',
    fontSize: '1rem',
    minHeight: '44px',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 6px 8px rgba(0, 0, 0, 0.15)',
    },
    transition: 'all 0.2s ease',
  },
  extraContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: theme.spacing(1.2),
    marginTop: theme.spacing(1),
  },
  appLinksContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    width: '100%',
  },
  appLink: {
    height: '50px',
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
      transform: 'scale(1.05)',
    },
  },
  secondaryButton: {
    fontWeight: 600,
    borderRadius: '8px',
    padding: '12px 0',
    textTransform: 'none',
  },
}));

const LoginPage = () => {
  const revenda = useSelector((state) => state.session.revenda);
  const serverAttributes = useSelector((state) => state.session.server.attributes);
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const t = useTranslation();

  const { languages, language, setLocalLanguage } = useLocalization();
  const languageList = Object.entries(languages).map(([code, { country, name }]) => ({
    code, country, name,
  }));

  const [failed, setFailed] = useState(false);
  const [email, setEmail] = usePersistedState('loginEmail', '');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showServerTooltip, setShowServerTooltip] = useState(false);
  const [showQr, setShowQr] = useState(false);

  const registrationEnabled = useSelector((state) => state.session.server.registration);
  const languageEnabled = useSelector((state) => {
    const attributes = state.session.server.attributes;
    return !attributes.language && !attributes['ui.disableLoginLanguage'];
  });
  const changeEnabled = useSelector((state) => !state.session.server.attributes.disableChange);
  const emailEnabled = useSelector((state) => state.session.server.emailEnabled);
  const openIdEnabled = useSelector((state) => state.session.server.openIdEnabled);
  const openIdForced = useSelector((state) => state.session.server.openIdEnabled && state.session.server.openIdForce);
  const [codeEnabled, setCodeEnabled] = useState(false);

  const [announcementShown, setAnnouncementShown] = useState(false);
  const announcement = useSelector((state) => state.session.server.announcement);

  const handlePasswordLogin = async (event) => {
    event.preventDefault();
    setFailed(false);
    try {
      const query = `email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`;
      const response = await fetch('/api/session', {
        method: 'POST',
        body: new URLSearchParams(code.length ? `${query}&code=${code}` : query),
      });
      if (response.ok) {
        const user = await response.json();
        generateLoginToken();
        dispatch(sessionActions.updateUser(user));
        const target = window.sessionStorage.getItem('postLogin') || '/';
        window.sessionStorage.removeItem('postLogin');
        navigate(target, { replace: true });
      } else if (response.status === 401 && response.headers.get('WWW-Authenticate') === 'TOTP') {
        setCodeEnabled(true);
      } else {
        throw Error(await response.text());
      }
    } catch {
      setFailed(true);
      setPassword('');
    }
  };

  const handleTokenLogin = useCatch(async (token) => {
    const response = await fetchOrThrow(`/api/session?token=${encodeURIComponent(token)}`);
    const user = await response.json();
    dispatch(sessionActions.updateUser(user));
    navigate('/');
  });

  const handleOpenIdLogin = () => {
    document.location = '/api/session/openid/auth';
  };

  useEffect(() => nativePostMessage('authentication'), []);

  useEffect(() => {
    const listener = (token) => handleTokenLogin(token);
    handleLoginTokenListeners.add(listener);
    return () => handleLoginTokenListeners.delete(listener);
  }, []);

  useEffect(() => {
    if (window.localStorage.getItem('hostname') !== window.location.hostname) {
      window.localStorage.setItem('hostname', window.location.hostname);
      setShowServerTooltip(true);
    }
  }, []);

  return (
    <LoginLayout>
      <div className={classes.options}>
        {(nativeEnvironment || window.location.hostname !== 'localhost') && changeEnabled && (
          <IconButton color="primary" onClick={() => navigate('/change-server')}>
            <Tooltip
              title={`${t('settingsServer')}: ${window.location.hostname}`}
              open={showServerTooltip}
              arrow
            >
              <VpnLockIcon />
            </Tooltip>
          </IconButton>
        )}
        {!nativeEnvironment && (
          <IconButton color="primary" onClick={() => setShowQr(true)}>
            <QrCode2Icon />
          </IconButton>
        )}
        {languageEnabled && (
          <FormControl>
            <Select
              value={language}
              onChange={(e) => setLocalLanguage(e.target.value)}
              sx={{
                bgcolor: theme.palette.background.paper,
                color: theme.palette.text.primary,
              }}
            >
              {languageList.map((it) => (
                <MenuItem key={it.code} value={it.code}>
                  <Box component="span" sx={{ mr: 1 }}>
                    <ReactCountryFlag countryCode={it.country} svg />
                  </Box>
                  {it.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </div>

      <AuthCard>
        <LogoImage />
        {!openIdForced ? (
          <>
            <TextField
              required
              error={failed}
              placeholder={t('userEmail')}
              name="email"
              value={email}
              autoComplete="email"
              autoFocus={!email}
              variant="standard"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountCircle />
                  </InputAdornment>
                ),
                className: classes.input,
                disableUnderline: true,
              }}
              onChange={(e) => setEmail(e.target.value)}
              helperText={failed && t('loginFailed')}
            />
            <TextField
              required
              error={failed}
              placeholder={t('userPassword')}
              name="password"
              value={password}
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              autoFocus={!!email}
              variant="standard"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
                className: classes.input,
                disableUnderline: true,
              }}
              onChange={(e) => setPassword(e.target.value)}
            />
            {codeEnabled && (
              <TextField
                required
                error={failed}
                placeholder={t('loginTotpCode')}
                name="code"
                value={code}
                type="number"
                variant="standard"
                onChange={(e) => setCode(e.target.value)}
              />
            )}
            <Button
              onClick={handlePasswordLogin}
              type="submit"
              variant="contained"
              color="primary"
              disabled={!email || !password || (codeEnabled && !code)}
              className={classes.loginButton}
            >
              {t('loginLogin')}
            </Button>
            {openIdEnabled && (
              <Button
                onClick={handleOpenIdLogin}
                variant="outlined"
              >
                {t('loginOpenId')}
              </Button>
            )}
            <div className={classes.extraContainer}>
              {registrationEnabled && (
                <Button
                  onClick={() => navigate('/register')}
                  variant="outlined"
                  className={classes.secondaryButton}
                  fullWidth
                >
                  {t('loginRegister')}
                </Button>
              )}
              {emailEnabled && (
                <Button
                  onClick={() => navigate('/reset-password')}
                  variant="outlined"
                  className={classes.secondaryButton}
                  fullWidth
                >
                  {t('loginReset')}
                </Button>
              )}
            </div>

            {(revenda?.googleplay || revenda?.applestore || serverAttributes?.androidAppLink || serverAttributes?.iosAppLink) && (
              <Box className={classes.appLinksContainer}>
                {revenda?.googleplay || serverAttributes?.androidAppLink ? (
                  <a
                    href={revenda?.googleplay || serverAttributes?.androidAppLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img src={PlayStoreImage} alt="Google Play" className={classes.appLink} />
                  </a>
                ) : null}
                {revenda?.applestore || serverAttributes?.iosAppLink ? (
                  <a
                    href={revenda?.applestore || serverAttributes?.iosAppLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img src={AppStoreImage} alt="App Store" className={classes.appLink} />
                  </a>
                ) : null}
              </Box>
            )}
          </>
        ) : (
          <Button
            onClick={handleOpenIdLogin}
            variant="contained"
            color="primary"
            className={classes.loginButton}
          >
            {t('loginOpenId')}
          </Button>
        )}
      </AuthCard>

      <QrCodeDialog open={showQr} onClose={() => setShowQr(false)} />

      <Snackbar
        open={!!announcement && !announcementShown}
        message={announcement}
        action={(
          <IconButton size="small" color="inherit" onClick={() => setAnnouncementShown(true)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      />
    </LoginLayout>
  );
};

export default LoginPage;
